import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { first, map } from 'rxjs/operators';

import { RootState } from '@root/app.state';
import * as CoreActions from '@core/core.actions';
import * as PlatformActions from '@platform/platform.actions';
import { selectUser } from '@platform/platform.selectors';
import { CurrentUser } from '@platform/platform.state';
import { go } from '@platform/router.actions';
import { RouteGuard } from '@platform/routing/route.guard';
import { selectUserProfile } from '@core/core.selectors';
import { firstValueFrom } from 'rxjs';
import { ResourceStatuses } from '@platform/shared/resource.state';

@Injectable({
  providedIn: 'root',
})
export class UserProfileGuard implements RouteGuard {
  constructor(private store$: Store<RootState>) {}

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
    return await this.loadAndWaitForOrganizationUnits(route);
  }

  async canActivateChild(childRoute: ActivatedRouteSnapshot): Promise<boolean> {
    return await this.loadAndWaitForOrganizationUnits(childRoute.parent!);
  }

  private async loadAndWaitForOrganizationUnits(route: ActivatedRouteSnapshot): Promise<boolean> {
    let userId = route.paramMap.get('userId');

    if (!userId) {
      const user = await this.getUser();
      userId = user.id;
    }

    this.store$.dispatch(CoreActions.loadUserProfile({ userId }));
    this.store$.dispatch(CoreActions.loadActiveUsers());
    this.store$.dispatch(CoreActions.loadUserOrganizationUnits());

    const userProfileState = await firstValueFrom(
      this.store$.pipe(
        select(selectUserProfile),
        first((x) => x.status === ResourceStatuses.FAILED || x.status === ResourceStatuses.SUCCEEDED),
      ),
    );
    this.store$.dispatch(CoreActions.clearCreatedUser());

    if (userProfileState.status !== ResourceStatuses.SUCCEEDED) {
      this.store$.dispatch(go({ path: ['error'] }));
    }

    return true;
  }

  private async getUser(): Promise<CurrentUser> {
    this.store$.dispatch(PlatformActions.loadCurrentUser());

    return await firstValueFrom(
      this.store$.pipe(
        select(selectUser),
        first((x) => x.status === ResourceStatuses.FAILED || x.status === ResourceStatuses.SUCCEEDED),
        map((state) => state.data),
      ),
    );
  }
}
